import { DateTime } from 'luxon'
import { isEmpty } from 'lodash-es'

const commentState = () => ({
  comments: new Map(),
  isLoading: false,
  totalCount: 0,
  currentPage: 1,
  isInfiniteLoading: false,
  hasMoreElements: false,
  isWatchingRealtime: false,
})

const createInitialState = () => ({
  questions: commentState(),
  shouldScroll: true,
  scrollIntention: false,
})

const getQuestionsAsMap = (questions) =>
  new Map(
    (questions ?? []).map((question) => [
      question.comment_id.toString(),
      question,
    ])
  )

const hasMoreElements = (comments, action) => comments.size < action.totalCount

const withoutComment = (comments, commentId) => {
  if (!comments.has(commentId)) return comments

  const next = new Map(comments)
  next.delete(commentId)
  return next
}

const mergeState = (state, newState) => ({
  ...state,
  questions: {
    ...state.questions,
    ...newState,
  },
})

const addSingleComment = (action, state) => {
  const newComments = getQuestionsAsMap([action.comment])
  return new Map([...newComments, ...state.questions.comments])
}

const injectFirstMessage = (injectFirstMessageId, collection) => {
  if (!injectFirstMessageId) {
    return collection
  }

  return new Map([
    [
      injectFirstMessageId,
      {
        comment_id: injectFirstMessageId,
        comment_type: 'questions',
        content_text: '',
        name: '',
        entry_date_ansi: DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss'),
      },
    ],
    ...collection,
  ])
}

// eslint-disable-next-line import-x/no-default-export
export default ({ actionNames, injectFirstMessageId }) =>
  (maybeState, action) => {
    const state = maybeState ?? createInitialState()

    switch (action.type) {
      case actionNames.INIT_QUESTIONS: {
        return mergeState(state, {
          isLoading: !action.doNotFetchQuestions,
          presenterToken: action.presenterToken,
        })
      }
      case actionNames.FETCH_QUESTIONS_SUCCESS: {
        if (isEmpty(action.comments) && parseInt(action.totalCount, 10) === 0) {
          return mergeState(state, {
            comments: injectFirstMessage(injectFirstMessageId, new Map()),
            isLoading: false,
            totalCount: action.totalCount,
            hasMoreElements: false,
          })
        }

        const newComments = getQuestionsAsMap(action.comments)
        const comments = new Map([...newComments, ...state.questions.comments])

        return mergeState(state, {
          comments: injectFirstMessage(injectFirstMessageId, comments),
          isLoading: false,
          totalCount: action.totalCount,
          hasMoreElements: hasMoreElements(comments, action),
        })
      }
      case actionNames.FETCH_QUESTIONS_FAILURE: {
        return mergeState(state, {
          isLoading: false,
        })
      }
      case actionNames.INFINITE_LOAD_QUESTIONS_SUCCESS: {
        const newComments = getQuestionsAsMap(action.comments)
        const comments = new Map([...state.questions.comments, ...newComments])

        return mergeState(state, {
          comments,
          totalCount: action.totalCount,
          isInfiniteLoading: false,
          currentPage: action.page,
          hasMoreElements: hasMoreElements(comments, action),
        })
      }
      case actionNames.INFINITE_LOAD_QUESTIONS_FAILURE: {
        return mergeState(state, {
          isInfiniteLoading: false,
        })
      }
      case actionNames.RESET_QUESTIONS: {
        return mergeState(state, commentState())
      }
      case actionNames.DELETE_QUESTION: {
        const comments = withoutComment(
          state.questions.comments,
          action.commentId
        )

        return mergeState(state, {
          comments,
        })
      }
      case actionNames.INFINITE_LOAD_QUESTIONS: {
        return mergeState(state, {
          isInfiniteLoading: true,
        })
      }
      case actionNames.REALTIME_ADD_QUESTION: {
        return mergeState(state, {
          comments: addSingleComment(action, state),
        })
      }
      case actionNames.REALTIME_REMOVE_COMMENT: {
        const { commentId: commentIdInt } = action
        const commentId = commentIdInt.toString()

        if (state.questions.comments.has(commentId)) {
          const comments = withoutComment(state.questions.comments, commentId)

          return {
            ...state,
            questions: {
              ...state.questions,
              comments,
            },
          }
        }

        return state
      }
      case actionNames.FETCH_SINGLE_QUESTION_SUCCESS: {
        return mergeState(state, {
          comments: addSingleComment(action, state),
        })
      }
      case actionNames.OPTIMISTICALLY_ADD_QUESTION: {
        return mergeState(state, {
          comments: addSingleComment(action, state),
        })
      }
      case actionNames.WATCH_REALTIME_QUESTIONS: {
        return mergeState(state, {
          isWatchingRealtime: true,
        })
      }
      case actionNames.SET_SCROLLING_INTENTION: {
        return {
          ...state,
          scrollIntention: action.scrollIntention,
        }
      }
      case actionNames.SET_SHOULD_SCROLL: {
        return {
          ...state,
          shouldScroll: action.shouldScroll,
        }
      }
      case actionNames.RESET_SCROLLING_PROPS: {
        return {
          ...state,
          scrollIntention: false,
          shouldScroll: false,
        }
      }
      case actionNames.OPTIMISTICALLY_UPDATE_QUESTION_COMMENTS: {
        const { comments } = action

        return {
          ...state,
          questions: {
            ...state.questions,
            comments: getQuestionsAsMap(comments),
          },
        }
      }
      default:
        return state
    }
  }
